import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import { Footer, HeaderMiddle, Navbar, TopBar, WA_NUMBER } from '../components/SiteChrome'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { AdNewsProofSection } from '../components/HomeNewsSection'
import { whatsAppCtaProps, track } from '../lib/analytics'
import { listDashboardNewsPosts } from '../lib/news'
import {
  getStorefrontConfig,
  listStorefrontCategories,
  placeholderImage,
  toExternalImage,
} from '../lib/dropify'
import type { StorefrontCategory } from '../lib/dropify'
import {
  INDEXABLE_ROBOTS,
  DEFAULT_OG_IMAGE,
  absoluteUrl,
  brandTitle,
  buildBreadcrumbSchema,
  buildFaqSchema,
} from '../lib/seo'

// Halaman untuk visitor yang mencari "importir": Dropify sebagai penyedia
// barang-barang impor. Desainnya sengaja beda dari halaman kartu-putih lain:
// dunia gudang kargo. Elemen tanda tangannya kartu "Manifes Gudang" di hero,
// pita kargo kuning sebagai pembatas, label dan angka bergaya packing list
// (monospace), rute barang yang membagi "urusan kami" vs "urusan kamu", dan
// tile kategori berfoto asli. Angle copy: barang impornya sudah di gudang,
// tinggal diambil untuk dijual lagi.
const SEO_TITLE = 'Cari Pemasok Barang Impor untuk Kulakan'
const SEO_DESCRIPTION =
  'Dropify mendistribusikan barang impor untuk pembelian grosir dari Surabaya. Bandingkan kategori, MOQ, stok, dan quotation melalui WhatsApp.'

const LEAD_SOURCE = 'landing_page' as const

const INK = '#0B1220'
const PANEL = '#121B2E'
const CARGO = '#F5B301'

const TICKER_ITEMS = [
  'Mulai 10 lusin',
  'Quotation dalam Rupiah',
  'Stok dikonfirmasi admin',
  'Kirim seluruh Indonesia',
  'Gudang Surabaya',
  'Pembelian dalam negeri',
]

// Pembagian kerja: apa yang sudah beres di sisi Dropify vs satu-satunya
// langkah pembeli. PENTING: Dropify BUKAN importir, jadi tidak ada klaim
// mengurus kontainer/bea cukai. Klaimnya: barang impor sudah ready di gudang,
// distok partai besar dari tangan pertamanya.
const ROUTE_OURS = [
  { step: 'Katalog per kategori', detail: 'Produk dikelompokkan menurut kategori, merek, dan kode model' },
  { step: 'Stok diperiksa', detail: 'Admin mengonfirmasi produk dan jumlah sebelum pembayaran' },
  { step: 'Quotation dalam Rupiah', detail: 'Harga, jumlah, dan ongkir dirinci dalam penawaran tertulis' },
  { step: 'Pengiriman dari Surabaya', detail: 'Estimasi mengikuti kota tujuan dan layanan ekspedisi' },
]

const FAQ_ITEMS = [
  {
    q: 'Apakah Dropify menjual barang impor?',
    a: 'Ya. Dropify mendistribusikan sejumlah produk impor untuk pembelian grosir dalam negeri. Dropify bukan penyedia jasa impor. Stok, MOQ, dan harga dikonfirmasi per produk melalui WhatsApp.',
  },
  {
    q: 'Bagaimana membandingkan harganya?',
    a: 'Harga mengikuti produk dan jumlah pesanan. Minta quotation, lalu bandingkan menggunakan SKU, isi kemasan, jumlah, ongkir, dan biaya penjualan yang sama.',
  },
  {
    q: 'Berapa minimum order di Dropify?',
    a: 'MOQ berbeda per kategori. Handuk umumnya mulai 10 lusin, sedangkan kaos kaki umumnya 50 lusin. Periksa halaman minimum order dan konfirmasikan ketentuan produk kepada admin.',
  },
  {
    q: 'Bisakah memulai sesuai MOQ untuk menguji pasar?',
    a: 'Bisa. Pilih satu kategori, periksa MOQ dan ketentuan campur varian, lalu restock berdasarkan kode produk yang benar-benar terjual di toko Anda.',
  },
  {
    q: 'Saya biasa ambil partai besar. Dilayani juga?',
    a: 'Bisa. Kirim produk, jumlah, komposisi varian, dan kota tujuan. Admin akan memeriksa kemampuan pemenuhan dan menyiapkan quotation sesuai volume tersebut.',
  },
  {
    q: 'Cara order bagaimana, dan kirim ke mana saja?',
    a: 'Pemesanan dan konfirmasi pembayaran dilakukan melalui WhatsApp. Barang dikirim dari Surabaya. Waktu penyiapan dan estimasi perjalanan dikonfirmasi berdasarkan isi pesanan, kota tujuan, dan layanan ekspedisi.',
  },
]

export const Route = createFileRoute('/cari-importir')({
  loader: async () => {
    const [config, categories, newsPosts] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontCategories().catch(() => [] as StorefrontCategory[]),
      listDashboardNewsPosts().catch(() => []),
    ])
    return { config, categories, newsPosts }
  },
  head: () => {
    const title = brandTitle(SEO_TITLE)
    return {
      meta: [
        { title },
        { name: 'description', content: SEO_DESCRIPTION },
        { name: 'robots', content: INDEXABLE_ROBOTS },
        { property: 'og:title', content: title },
        { property: 'og:description', content: SEO_DESCRIPTION },
        { property: 'og:url', content: absoluteUrl('/cari-importir') },
        { property: 'og:image', content: DEFAULT_OG_IMAGE },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: SEO_DESCRIPTION },
        { name: 'twitter:image', content: DEFAULT_OG_IMAGE },
      ],
      links: [{ rel: 'canonical', href: absoluteUrl('/cari-importir') }],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema(FAQ_ITEMS.map((f) => ({ q: f.q, a: f.a })))),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: 'Beranda', path: '/' },
              { name: 'Cari Importir', path: '/cari-importir' },
            ]),
          ),
        },
      ],
    }
  },
  component: ImportirPage,
})

function WaCta({ placement, label }: { placement: string; label: string }) {
  const waHref = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
    'Halo Dropify, saya mencari barang impor untuk dijual kembali. Produk: ___. Jumlah: ___. Kota tujuan: ___. Mohon cek stok dan quotation.',
  )}`
  return (
    <a
      href={waHref}
      {...whatsAppCtaProps(LEAD_SOURCE, { cta_placement: placement, lp_category: 'Cari Importir' })}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center justify-center gap-2.5 rounded-lg bg-green-700 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-green-900/40 transition-colors hover:bg-green-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-400"
    >
      <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5" />
      {label}
    </a>
  )
}

/** Pita kargo kuning: pembatas antar section, berisi fakta gudang. */
function CargoTape() {
  const line = TICKER_ITEMS.map((item) => `${item}  ✦  `).join('')
  return (
    <div
      aria-hidden
      className="overflow-hidden border-y-2 border-black/20 py-2.5"
      style={{ backgroundColor: CARGO }}
    >
      <div className="importir-tape flex w-max whitespace-nowrap font-mono text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
        <span>{line}</span>
        <span>{line}</span>
      </div>
    </div>
  )
}

/** Kartu manifes gudang: elemen tanda tangan halaman ini. */
function ManifestCard({ categories }: { categories: StorefrontCategory[] }) {
  const rows = categories.slice(0, 6)
  return (
    <div className="relative rounded-lg border border-slate-700 bg-slate-50 p-5 font-mono text-slate-800 shadow-2xl shadow-black/50">
      <div className="flex items-baseline justify-between border-b-2 border-dashed border-slate-300 pb-3">
        <p className="text-xs font-bold uppercase tracking-[0.25em]">Manifes Gudang</p>
        <p className="text-[10px] uppercase tracking-widest text-slate-500">Surabaya, ID</p>
      </div>
      <table className="mt-3 w-full text-xs">
        <thead>
          <tr className="text-left uppercase tracking-widest text-slate-400">
            <th className="pb-2 font-normal">Barang</th>
            <th className="pb-2 text-right font-normal">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((category) => (
            <tr key={category.id} className="border-t border-dotted border-slate-300">
              <td className="py-2 pr-2">
                <a
                  href={`/kategori/${category.slug}`}
                  onClick={() =>
                    track('click_internal_link', {
                      link_url: `/kategori/${category.slug}`,
                      link_text: category.name,
                      link_location: 'importir_manifest',
                    })
                  }
                  className="hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
                >
                  {category.name}
                </a>
              </td>
              <td className="py-2 text-right">
                <span className="inline-block rounded-sm bg-green-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-green-700">
                  Cek admin
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 border-t-2 border-dashed border-slate-300 pt-3 text-[10px] uppercase tracking-widest text-slate-500">
        Ambil mulai hitungan lusin
      </div>
      {/* Stempel */}
      <div
        className="pointer-events-none absolute -right-3 -bottom-4 rotate-[-8deg] rounded border-4 px-3 py-1.5 font-mono text-xs font-black uppercase tracking-[0.15em]"
        style={{ borderColor: CARGO, color: CARGO, backgroundColor: 'rgba(11,18,32,0.85)' }}
      >
        Quotation Grosir
      </div>
    </div>
  )
}

function FaqRow({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b border-slate-800">
      <button
        type="button"
        onClick={() => {
          setOpen((value) => {
            if (!value) track('faq_open', { faq_question: q, faq_location: 'importir' })
            return !value
          })
        }}
        className="flex w-full items-center justify-between gap-4 py-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow-400"
      >
        <span className="font-mono text-base font-bold text-slate-100">{q}</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={`h-3.5 w-3.5 flex-shrink-0 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && <p className="pb-5 text-base leading-relaxed text-slate-400">{a}</p>}
    </div>
  )
}

function ImportirPage() {
  const { config, categories, newsPosts } = Route.useLoaderData()

  return (
    <div className="min-h-screen" style={{ backgroundColor: INK }}>
      <style>{`
        @keyframes importir-tape-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .importir-tape { animation: importir-tape-scroll 30s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .importir-tape { animation: none; } }
      `}</style>

      <TopBar config={config} />
      <HeaderMiddle />
      <Navbar />

      {/* ── Hero: pernyataan + manifes gudang ─────────────────────────────── */}
      <header className="mx-auto max-w-7xl px-4 pb-16 pt-12 lg:pb-20 lg:pt-16">
        <nav className="mb-8 flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest text-slate-500">
          <a href="/" className="hover:text-white transition-colors">Beranda</a>
          <FontAwesomeIcon icon={faChevronRight} className="h-2.5 w-2.5" />
          <span className="text-slate-300">Cari Importir</span>
        </nav>
        <div className="grid items-center gap-12 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.3em]" style={{ color: CARGO }}>
              Penyedia Barang Impor
            </p>
            <h1 className="mt-4 text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Cari barang impor?
              <br />
              Barangnya sudah
              <br />
              <span style={{ color: CARGO }}>di gudang kami.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400">
              Dropify menyediakan sejumlah kaos kaki, pakaian dalam, handuk, mukena, dan keset
              impor untuk pembelian grosir dalam negeri. Bandingkan produk, MOQ, dan satuannya,
              lalu minta admin memeriksa stok serta menyiapkan quotation.
            </p>
            <div className="mt-8">
              <WaCta placement="hero" label="Minta quotation barang impor" />
            </div>
          </div>
          <div className="px-2 sm:px-8 lg:px-0">
            <ManifestCard categories={categories} />
          </div>
        </div>
      </header>

      <WhatsAppChannelSection showSocksPromo={false} />

      <CargoTape />

      {/* ── Rute barang: urusan kami vs urusan kamu ───────────────────────── */}
      <section className="mx-auto max-w-7xl px-4 py-16 lg:py-20">
        <h2 className="text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">
          Beli barang impor melalui distributor dalam negeri
        </h2>
        <p className="mt-2 max-w-2xl text-base leading-relaxed text-slate-400">
          Dropify bukan penyedia jasa impor. Halaman ini ditujukan bagi reseller yang ingin membeli
          barang impor dalam jumlah grosir melalui transaksi dalam negeri.
        </p>

        <div className="mt-10 grid gap-8 lg:grid-cols-[3fr_1fr]">
          {/* Urusan kami */}
          <div>
            <p className="mb-4 inline-block rounded-sm px-2 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-slate-900" style={{ backgroundColor: CARGO }}>
              Sudah beres di gudang
            </p>
            <ul className="grid gap-px overflow-hidden rounded-lg border border-slate-800 sm:grid-cols-2 lg:grid-cols-4">
              {ROUTE_OURS.map((item) => (
                <li key={item.step} className="p-5" style={{ backgroundColor: PANEL }}>
                  <p className="font-mono text-[11px] uppercase tracking-widest text-green-500">
                    ✓ Proses jelas
                  </p>
                  <p className="mt-2 font-mono text-base font-bold text-slate-100">{item.step}</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{item.detail}</p>
                </li>
              ))}
            </ul>
          </div>
          {/* Urusan kamu */}
          <div>
            <p className="mb-4 inline-block rounded-sm bg-green-500 px-2 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-slate-900">
              Data dari Anda
            </p>
            <div className="flex h-[calc(100%-2.25rem)] min-h-[10rem] flex-col justify-between rounded-lg border-2 border-dashed border-green-600/60 p-5">
              <div>
                <p className="font-mono text-base font-bold text-slate-100">Chat admin, sebut barangnya</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">
                  Kirim kategori, kode produk, jumlah, dan kota tujuan. Gunakan riwayat penjualan
                  toko sebagai dasar untuk permintaan restock.
                </p>
              </div>
              <div className="mt-4">
                <WaCta placement="route" label="Kirim daftar barang" />
              </div>
            </div>
          </div>
        </div>

        <p className="mt-8 max-w-2xl font-mono text-xs leading-relaxed text-slate-500">
          Transaksi dilakukan dalam Rupiah melalui rekening yang diinformasikan admin. Pastikan
          kode produk, satuan, jumlah, harga, dan ongkir sudah tercantum pada quotation.
        </p>
      </section>

      <CargoTape />

      <AdNewsProofSection posts={newsPosts} />

      {/* ── Isi gudang: tile kategori berfoto ─────────────────────────────── */}
      <section className="mx-auto max-w-7xl px-4 py-16 lg:py-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">
              Isi gudangnya
            </h2>
            <p className="mt-2 text-base text-slate-400">
              Buka kategori, pilih produk, lalu hitung margin menggunakan quotation dan seluruh biaya penjualan.
            </p>
          </div>
          <p className="font-mono text-xs uppercase tracking-widest text-slate-500">
            Minimum order beda tiap kategori.{' '}
            <a
              href="/minimum-order"
              onClick={() =>
                track('click_internal_link', {
                  link_url: '/minimum-order',
                  link_text: 'Lihat ketentuannya',
                  link_location: 'importir',
                })
              }
              className="underline decoration-dotted underline-offset-4 hover:text-slate-300"
              style={{ color: CARGO }}
            >
              Lihat ketentuannya
            </a>
          </p>
        </div>
        <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {categories.map((category) => (
            <a
              key={category.id}
              href={`/kategori/${category.slug}`}
              onClick={() =>
                track('click_internal_link', {
                  link_url: `/kategori/${category.slug}`,
                  link_text: category.name,
                  link_location: 'importir_grid',
                })
              }
              className="group overflow-hidden rounded-lg border border-slate-800 transition-colors hover:border-yellow-500/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow-400"
              style={{ backgroundColor: PANEL }}
            >
              <img
                src={
                  toExternalImage(category.image_url, {
                    width: 360,
                    height: 280,
                    smart: true,
                    format: 'webp',
                    quality: 60,
                  }) || placeholderImage(category.name, 360, 280)
                }
                alt={`Barang impor ${category.name}`}
                width={360}
                height={280}
                loading="lazy"
                decoding="async"
                className="aspect-[9/7] w-full object-cover opacity-90 transition duration-300 group-hover:scale-[1.03] group-hover:opacity-100"
              />
              <div className="flex items-center justify-between gap-2 px-3 py-2.5">
                <p className="truncate font-mono text-xs font-bold uppercase tracking-wider text-slate-200">
                  {category.name}
                </p>
                <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-green-500" aria-hidden />
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-3xl px-4 py-16 lg:py-20">
        <h2 className="text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">
          Tanya jawab
        </h2>
        <div className="mt-6 border-t border-slate-800">
          {FAQ_ITEMS.map((item) => (
            <FaqRow key={item.q} q={item.q} a={item.a} />
          ))}
        </div>
      </section>

      {/* ── Penutup: band kuning ──────────────────────────────────────────── */}
      <section className="border-y-2 border-black/20" style={{ backgroundColor: CARGO }}>
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 py-12 sm:flex-row sm:items-center">
          <div>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-slate-900/70">
              Pemesanan melalui WhatsApp
            </p>
            <p className="mt-2 max-w-xl text-2xl font-black uppercase leading-tight tracking-tight text-slate-900">
              Sebutkan produk, jumlah, dan kota tujuan. Admin akan memeriksa stok dan menyiapkan quotation.
            </p>
          </div>
          <a
            href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(
              'Halo Dropify, saya mencari barang impor untuk dijual kembali. Produk: ___. Jumlah: ___. Kota tujuan: ___. Mohon cek stok dan quotation.',
            )}`}
            {...whatsAppCtaProps(LEAD_SOURCE, { cta_placement: 'closing', lp_category: 'Cari Importir' })}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex flex-shrink-0 items-center justify-center gap-2.5 rounded-lg bg-slate-900 px-7 py-4 text-sm font-bold text-white transition-colors hover:bg-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          >
            <FontAwesomeIcon icon={faWhatsapp} className="h-5 w-5 text-green-400" />
            Chat Admin Sekarang
          </a>
        </div>
      </section>

      <Footer config={config} categories={categories} />
    </div>
  )
}
